/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { useRef, useState } from 'react';
import { Button, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type {
  StatisticsDateRange,
  StatisticsDateRangeStatus
} from '../types/date-range';

interface Props {
  value: StatisticsDateRange;
  onChange: (range: StatisticsDateRange) => void;
  status?: StatisticsDateRangeStatus;
  onRetry?: () => void;
  showApply?: boolean;
  subject?: 'profile' | 'access';
}

const reportingDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Ho_Chi_Minh',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});
const isFutureDate = (date: Dayjs) =>
  date.format('YYYY-MM-DD') > reportingDateFormatter.format(new Date());

export function StatisticsDateRangePicker({
  value,
  onChange,
  status = 'idle',
  onRetry,
  showApply = false,
  subject = 'access'
}: Props) {
  const [open, setOpen] = useState(false);
  const opened = useRef(false);
  const draft = useRef<[Dayjs | null, Dayjs | null] | null>(null);
  const commit = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates?.some((date) => date && isFutureDate(date))) return;
    onChange([
      dates?.[0]?.format('YYYY-MM-DD') ?? null,
      dates?.[1]?.format('YYYY-MM-DD') ?? null
    ]);
  };
  return (
    <div
      role='group'
      aria-label={
        subject === 'profile'
          ? 'Khoảng ngày tạo hồ sơ'
          : 'Khoảng thời điểm truy cập'
      }
      aria-busy={status === 'loading'}
    >
      <div css={controlStyle}>
        <DatePicker.RangePicker
          format='DD/MM/YYYY'
          disabledDate={isFutureDate}
          open={showApply ? open : undefined}
          onCalendarChange={(dates) => {
            if (showApply && opened.current) draft.current = dates;
          }}
          onOpenChange={(next) => {
            if (!showApply) return;
            if (next)
              draft.current = [
                value[0] ? dayjs(value[0]) : null,
                value[1] ? dayjs(value[1]) : null
              ];
            else if (opened.current) commit(draft.current);
            opened.current = next;
            setOpen(next);
          }}
          value={[
            value[0] ? dayjs(value[0]) : null,
            value[1] ? dayjs(value[1]) : null
          ]}
          order={false}
          allowEmpty={[true, true]}
          onChange={(dates) => {
            if (!showApply || !dates) commit(dates);
            else if (opened.current) draft.current = dates;
          }}
          placeholder={[status === 'loading' ? '' : 'Từ ngày', 'Đến ngày']}
          renderExtraFooter={
            showApply
              ? () => (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      padding: '6px 12px'
                    }}
                  >
                    <Button
                      type='primary'
                      size='small'
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        commit(draft.current);
                        opened.current = false;
                        setOpen(false);
                      }}
                    >
                      Áp dụng
                    </Button>
                  </div>
                )
              : undefined
          }
        />
        {status === 'loading' && (
          <span
            className='statistics-from-loading-dots'
            css={dotsStyle}
            aria-hidden='true'
          >
            <span />
            <span />
            <span />
          </span>
        )}
      </div>
      {status === 'loading' && (
        <div role='status' style={{ fontSize: 12 }}>
          {subject === 'profile'
            ? 'Đang tìm ngày tạo hồ sơ đầu tiên…'
            : 'Đang tìm thời điểm truy cập đầu tiên…'}
        </div>
      )}
      {status === 'empty' && (
        <div role='status' style={{ fontSize: 12 }}>
          {subject === 'profile'
            ? 'Không có hồ sơ trong khoảng đã chọn.'
            : 'Không có lượt truy cập trong khoảng đã chọn.'}
        </div>
      )}
      {status === 'error' && (
        <div role='status' style={{ fontSize: 12 }}>
          Chưa tải được Từ ngày.
          <Button type='link' size='small' onClick={onRetry}>
            Thử lại
          </Button>
        </div>
      )}
    </div>
  );
}
const controlStyle = css`
  position: relative;
  display: inline-flex;
`;
const wave = keyframes`0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-3px);opacity:1}`;
const dotsStyle = css`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  gap: 3px;
  pointer-events: none;
  color: rgba(0, 0, 0, 0.45);
  > span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: ${wave} 900ms ease-in-out infinite;
  }
  > span:nth-of-type(2) {
    animation-delay: 150ms;
  }
  > span:nth-of-type(3) {
    animation-delay: 300ms;
  }
  @media (prefers-reduced-motion: reduce) {
    > span {
      animation: none;
      opacity: 0.7;
    }
  }
`;
